// frontend/src/utils/api.js
// Purpose: Centralized API communication functions
// Desired outcome: Provide a consistent interface for making API requests with improved handling for multipart/form-data

import axios from 'axios';
import logger from './logger';

// Determine the base URL for API requests based on environment
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Create axios instance with default config
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// Request interceptor for adding auth token and handling request config
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage if it exists
    const token = localStorage.getItem('authToken');
    
    // If token exists, add it to request headers
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Important: Don't override content-type for multipart/form-data requests
    // FormData sets its own boundary parameter which browsers handle automatically
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    // Handle request errors
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling common responses and errors
api.interceptors.response.use(
  (response) => {
    // Process successful responses
    return response;
  },
  (error) => {
    // Handle response errors
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('API Response Error:', error.response.data);
      
      // Handle authentication errors
      if (error.response.status === 401) {
        // Clear token and redirect to login if unauthorized
        localStorage.removeItem('authToken');
        // Redirect logic can be added here if needed
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('API No Response Error:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('API Setup Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Direct axios methods
api.get = async (url, config) => {
  const response = await axios.get(`${BASE_URL}${url}`, config);
  return response;
};

api.post = async (url, data, config) => {
  try {
    const response = await axios.post(`${BASE_URL}${url}`, data, config);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('POST request failed:', error);
    return { 
      success: false, 
      error: error.response?.data?.message || error.message 
    };
  }
};

// Utility functions for common API operations

// GET request wrapper
api.fetchData = async (endpoint) => {
  try {
    const response = await api.get(endpoint);
    return { success: true, data: response.data };
  } catch (error) {
    return { 
      success: false, 
      error: error.response?.data?.message || error.message 
    };
  }
};

// POST request wrapper
api.postData = async (endpoint, data) => {
  try {
    // Determine if we're sending FormData or JSON
    const headers = data instanceof FormData 
      ? { 'Content-Type': 'multipart/form-data' }
      : { 'Content-Type': 'application/json' };
    
    const response = await axios.post(`${BASE_URL}${endpoint}`, data, { headers });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('postData error:', error);
    return { 
      success: false, 
      error: error.response?.data?.message || error.message 
    };
  }
};

// PUT request wrapper
api.updateData = async (endpoint, data) => {
  try {
    const response = await api.put(endpoint, data);
    return { success: true, data: response.data };
  } catch (error) {
    return { 
      success: false, 
      error: error.response?.data?.message || error.message 
    };
  }
};

// DELETE request wrapper
api.deleteData = async (endpoint) => {
  try {
    const response = await api.delete(endpoint);
    return { success: true, data: response.data };
  } catch (error) {
    return { 
      success: false, 
      error: error.response?.data?.message || error.message 
    };
  }
};

// Function to construct the download URL
const constructDownloadUrl = (reportID, fileType, queryParams = '') => {
  // Ensure the reportID always has "report-" prefix
  const formattedReportID = reportID.startsWith('report-') ? reportID : `report-${reportID}`;

  // Ensure query parameters are formatted correctly
  let queryString = queryParams ? `&${queryParams.replace(/^\?/, '')}` : '';

  // Construct and return the properly formatted URL
  return `/api/reports/export/${fileType}?reportID=${formattedReportID}${queryString}`;
};

// Function to download a report file (PDF or Excel)
api.downloadFile = async (fileType, reportID) => {
  try {
      logger.info(`Downloading ${fileType.toUpperCase()} report...`);

      // Ensure reportID has "report-" prefix
      const formattedReportID = reportID.startsWith('report-') ? reportID : `report-${reportID}`;

      // Construct the final URL
      const finalURL = `${BASE_URL.replace(/\/$/, '')}${constructDownloadUrl(formattedReportID, fileType)}`;

      logger.info(`Final API Endpoint: ${finalURL}`);

      // Get authentication token
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Fetch the report file
      const response = await fetch(finalURL, { method: 'GET', headers });

      // Validate response
      if (!response.ok) {
          throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      // Convert response to Blob
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Determine file extension
      const fileExtension = fileType === 'pdf' ? 'pdf' : 'xlsx';

      // Trigger file download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `Report-${formattedReportID}.${fileExtension}`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
          document.body.removeChild(link);
      }, 100);

      return { success: true };

  } catch (error) {
      logger.error(`Error downloading ${fileType.toUpperCase()} report: ${error.message}`);
      return { success: false, error: error.message };
  }
};

// Export the API object
export default api;